import { parsePeriod } from '../params';
import type { Engine } from '../types';
import { buildUrl, dateBefore, joinQuery } from '../url';

export const LANGUAGES: Array<[token: string, name: string]> = [
  ['ts', 'TypeScript'],
  ['js', 'JavaScript'],
  ['py', 'Python'],
  ['go', 'Go'],
  ['rs', 'Rust'],
  ['rb', 'Ruby'],
  ['java', 'Java'],
  ['kt', 'Kotlin'],
  ['swift', 'Swift'],
  ['c', 'C'],
  ['cpp', 'C++'],
  ['cs', 'C#'],
  ['php', 'PHP'],
  ['sh', 'Shell'],
  ['lua', 'Lua'],
  ['zig', 'Zig'],
  ['ex', 'Elixir'],
  ['dart', 'Dart'],
  ['nix', 'Nix'],
];

export const github: Engine = {
  id: 'github',
  name: 'GitHub',
  bang: 'gh',
  aliases: ['github'],
  homepage: 'https://github.com',
  category: 'Developer',
  signIn: 'Code search needs you signed in to GitHub.',
  description: 'Repositories, code, issues and PRs.',
  params: [
    {
      id: 'type',
      type: 'choice',
      label: 'Search in',
      group: 'Type',
      options: [
        { value: 'repositories', token: 'repo', label: 'Repositories' },
        { value: 'code', token: 'code', label: 'Code' },
        { value: 'issues', token: 'issue', label: 'Issues' },
        { value: 'pullrequests', token: 'pr', label: 'Pull requests' },
        { value: 'discussions', token: 'disc', label: 'Discussions' },
        { value: 'users', token: 'user', label: 'Users' },
        { value: 'commits', token: 'commit', label: 'Commits' },
      ],
    },
    {
      id: 'language',
      when: {
        param: 'type',
        in: ['repositories', 'code', 'issues', 'pullrequests', 'users'],
        orUnset: true,
      },
      whenLabel: 'doesn’t apply to discussions or commits',
      type: 'choice',
      label: 'Language',
      group: 'Filters',
      options: LANGUAGES.map(([token, name]) => ({
        value: name,
        token,
        label: name,
      })),
    },
    {
      id: 'stars',
      when: { param: 'type', in: ['repositories'], orUnset: true },
      whenLabel: 'only applies to repositories',
      type: 'number',
      prefix: '*',
      label: 'Min stars',
      group: 'Filters',
      placeholder: '100',
      help: '*100 = at least 100 stars.',
    },
    {
      id: 'pushed',
      when: { param: 'type', in: ['repositories'], orUnset: true },
      whenLabel: 'only applies to repositories',
      type: 'period',
      label: 'Pushed within',
      group: 'Filters',
      units: ['d', 'w', 'm', 'y'],
      counted: true,
      help: 'Only repos with commits in this window: y = past year.',
    },
    {
      id: 'active',
      when: { param: 'type', in: ['repositories'], orUnset: true },
      whenLabel: 'only applies to repositories',
      type: 'flag',
      token: 'active',
      label: 'Exclude archived',
      group: 'Filters',
    },
    {
      id: 'open',
      when: { param: 'type', in: ['issues', 'pullrequests'] },
      whenLabel: 'only applies to issues and pull requests',
      type: 'flag',
      token: 'open',
      label: 'Open only',
      group: 'Filters',
      help: 'For issues and pull requests.',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        {
          value: 'stars',
          token: 'stars',
          label: 'Most stars',
          when: { param: 'type', in: ['repositories'], orUnset: true },
          whenLabel: 'only applies to repositories',
        },
        {
          value: 'updated',
          token: 'recent',
          label: 'Recently updated',
          when: {
            param: 'type',
            in: ['repositories', 'issues', 'pullrequests', 'discussions'],
            orUnset: true,
          },
          whenLabel: 'doesn’t apply to code, user or commit search',
        },
      ],
    },
  ],
  build({ query, values }) {
    let pushed: string | false = false;
    if (typeof values.pushed === 'string') {
      const [unit, count] = parsePeriod(values.pushed);
      pushed = `pushed:>=${dateBefore(unit, count)}`;
    }
    return buildUrl('https://github.com/search', {
      q: joinQuery(
        query,
        typeof values.language === 'string' &&
          `language:${values.language.includes(' ') || values.language.includes('+') || values.language.includes('#') ? `"${values.language}"` : values.language}`,
        typeof values.stars === 'number' && `stars:>=${values.stars}`,
        pushed,
        values.active === true && 'archived:false',
        values.open === true && 'is:open'
      ),
      type: values.type as string | undefined,
      s: values.sort as string | undefined,
      o: values.sort ? 'desc' : undefined,
    });
  },
};
